import { useCallback, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StickyNote, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

// Nástěnka zakázky: JEDEN formátovaný text, do kterého se píše — ne vlákno
// komentářů a ne historie příspěvků. Vidí ji každý, kdo má přístup k zakázce,
// edituje ji vedení. Kdyby přišel požadavek na diskuzi, je to jiná funkce.

const TOOLS = [
  { l: "B", title: "Tučně", cmd: "bold", cls: "font-extrabold" },
  { l: "I", title: "Kurzíva", cmd: "italic", cls: "italic font-semibold" },
  { l: "U", title: "Podtrženo", cmd: "underline", cls: "underline font-semibold" },
  { l: "H", title: "Nadpis", cmd: "formatBlock", arg: "<h3>", cls: "font-extrabold" },
  { l: "• seznam", title: "Odrážky", cmd: "insertUnorderedList", cls: "font-semibold" },
  { l: "1. seznam", title: "Číslovaný seznam", cmd: "insertOrderedList", cls: "font-semibold" },
  { l: "⟲", title: "Zpět", cmd: "undo", cls: "font-semibold" },
];

// Očista v prohlížeči je jen pohodlí (ať neodchází balast z Wordu). Skutečná
// obrana je sanitize_board_html na serveru — požadavek nemusí přijít z UI.
const ALLOWED = ["P", "BR", "STRONG", "B", "EM", "I", "U", "H3", "UL", "OL", "LI", "DIV", "SPAN"];
function sanitize(html) {
  const box = document.createElement("div");
  box.innerHTML = html || "";
  const walk = (node) => {
    [...node.children].forEach((el) => {
      walk(el);
      if (!ALLOWED.includes(el.tagName)) el.replaceWith(...el.childNodes);
      else [...el.attributes].forEach((a) => el.removeAttribute(a.name));
    });
  };
  walk(box);
  return box.innerHTML.trim();
}

export function ProjectBoard({ project, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const body = project.board_note || "";

  // DialogContent jede přes Radix Portal, který děti připojí až o render po
  // otevření — efekt na [open] proto psal do ještě neexistujícího prvku a
  // editor zůstal prázdný (a uložení pak přepsalo starou poznámku). Callback
  // ref plní editor přesně ve chvíli, kdy prvek vznikne. Obsah dialogu se při
  // zavření odpojí, takže každé otevření startuje z aktuálního `body`.
  const setEditor = useCallback((el) => {
    editorRef.current = el;
    if (el && el.dataset.init !== "1") {
      el.innerHTML = body || "<p><br></p>";
      el.dataset.init = "1";
    }
  }, [body]);

  const exec = (t) => {
    document.execCommand(t.cmd, false, t.arg || null);
    setDirty(true);
  };

  const save = async () => {
    // Pojistka: kdyby se editor přesto nenačetl, nikdy nesmíme přepsat starou
    // poznámku prázdným uložením.
    if (!editorRef.current?.textContent.trim() && body) {
      return toast.error("Poznámky se nenačetly, zavřete okno a otevřete ho znovu.");
    }
    setSaving(true);
    try {
      const html = sanitize(editorRef.current?.innerHTML || "");
      // Editor po smazání textu nechává <p><br></p> — prázdný obsah musí
      // dojít jako prázdný, jinak zůstane viset hlavička o úpravě ničeho.
      const clean = html.replace(/<[^>]*>|&nbsp;|\s/g, "") ? html : "";
      await api.put(`/projects/${project.id}/board`, { body_html: clean });
      toast.success(clean ? "Poznámky uloženy." : "Poznámky vyprázdněny.");
      setOpen(false);
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  };

  const meta = project.board_note_updated_by
    ? `Naposledy upravil ${project.board_note_updated_by} · ${formatDateTime(project.board_note_updated_at)}`
    : "";

  // Prázdnou nástěnku nemá smysl ukazovat tomu, kdo do ní stejně nesmí psát.
  if (!body && !canEdit) return null;

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-[20px] p-4 md:p-5" data-testid="project-board">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <StickyNote size={18} className="text-amber-600 dark:text-amber-400" /> Poznámky
        </h2>
        {meta && <span className="text-xs text-muted-foreground" data-testid="board-meta">{meta}</span>}
        <span className="flex-1" />
        {canEdit && (
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="board-edit-btn"
            className="h-11 w-full md:w-auto border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10">
            <Pencil size={15} /> {body ? "Upravit" : "Napsat poznámky"}
          </Button>
        )}
      </div>

      {body ? (
        <div data-testid="board-body" dangerouslySetInnerHTML={{ __html: body }}
          className="text-sm leading-relaxed break-words [&_p]:mb-2.5 [&_p:last-child]:mb-0 [&_h3]:font-bold [&_h3]:text-base [&_h3]:mb-1.5 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold" />
      ) : (
        <p className="text-sm text-muted-foreground">
          Co má tým o zakázce vědět: přístup na stavbu, klíče a kódy, domluvy s investorem, provozní pravidla.
        </p>
      )}

      <Dialog open={open} onOpenChange={async (v) => { if (!v && dirty && !await ask("Zavřít bez uložení?", { danger: true })) return; setOpen(v); setDirty(false); }}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="board-dialog">
          <DialogHeader>
            <DialogTitle>Upravit poznámky</DialogTitle>
            <DialogDescription>Jedny poznámky na zakázku — píše se do nich, ne pod sebe. Vidí je každý, kdo má přístup k zakázce.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-1.5 border-y border-border py-2">
            {TOOLS.map((t) => (
              <button key={t.l} type="button" title={t.title} data-testid={`board-tool-${t.cmd}`}
                onMouseDown={(e) => e.preventDefault()} onClick={() => exec(t)}
                className={`min-w-11 h-11 md:min-w-9 md:h-9 px-2.5 rounded-[10px] border border-border bg-card text-sm hover:border-brand hover:text-brand transition-colors ${t.cls}`}>
                {t.l}
              </button>
            ))}
            <span className="flex-1" />
            <span className={`text-xs font-bold ${dirty ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
              {dirty ? "Neuložené změny" : "Uloženo"}
            </span>
          </div>

          <div ref={setEditor} contentEditable suppressContentEditableWarning data-testid="board-editor"
            onInput={() => setDirty(true)}
            className="flex-1 overflow-y-auto min-h-[220px] md:min-h-[260px] bg-card border border-border rounded-[14px] p-4 text-sm leading-relaxed outline-none focus-visible:border-brand break-words [&_p]:mb-2.5 [&_h3]:font-bold [&_h3]:text-base [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:mb-1" />

          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { setDirty(false); setOpen(false); }} className="h-12 md:h-11 w-full md:w-auto">Zrušit</Button>
            <Button onClick={save} disabled={saving || !dirty} data-testid="board-save-btn" className="h-12 md:h-11 w-full md:w-auto font-bold">
              {saving ? "Ukládám…" : "Uložit změny"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
